import { useEffect, useState } from 'react';

/**
 * Hook that waits for a DOM element's position to stabilize before returning true.
 *
 * The sidebar dynamically loads navigation items, which pushes target elements
 * (e.g. "More" button) down. If a spotlight renders before the sidebar settles,
 * it appears at the wrong position.
 *
 * Checks the element's vertical position every 100ms and waits until it hasn't
 * changed for 300ms before signaling readiness.
 *
 * @param selector - CSS selector for the target element
 * @returns { isStable: boolean }
 */
export const useStableElementPosition = (selector: string): { isStable: boolean } => {
	const [isStable, setIsStable] = useState(false);

	useEffect(() => {
		if (isStable || !selector) {
			return;
		}

		let lastTop: number | null = null;
		let stableSince: number | null = null;

		const intervalId = setInterval(() => {
			const el = globalThis.document?.querySelector(selector);
			if (!el) {
				lastTop = null;
				stableSince = null;
				return;
			}

			const currentTop = el.getBoundingClientRect().top;
			const now = Date.now();

			if (lastTop === null || lastTop !== currentTop) {
				lastTop = currentTop;
				stableSince = now;
			} else if (stableSince !== null && now - stableSince >= 300) {
				setIsStable(true);
				clearInterval(intervalId);
			}
		}, 100);

		return () => clearInterval(intervalId);
	}, [selector, isStable]);

	return { isStable };
};
